import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, Shield } from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const PrivacyPage = () => {
  useCanonical('https://ecuflashservice.com/privacy');
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Privacy Policy | ECU Flash Service</title>
        <meta name="description" content="ECU Flash Service privacy policy. Learn how we collect, use, and protect your personal information and ECU files." />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <Link to="/" className="flex items-center gap-1 text-[#888] hover:text-[#00F0FF] transition text-sm font-semibold uppercase tracking-wider">
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-white/10 mb-6">
            <Shield className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#888]">Privacy</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
            <span className="text-white">Privacy</span>{' '}
            <span className="text-[#00F0FF]">Policy</span>
          </h1>
          <p className="text-[#888] text-sm">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-[#0A0A0A] border border-white/5 p-8 md:p-12">
          <div className="space-y-8">
            
            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">1. Introduction</h2>
              <p className="text-[#888] leading-relaxed">
                ECU Flash Service (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services. Please read this policy carefully to understand our practices regarding your personal data.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">2. Information We Collect</h2>
              <p className="text-[#888] leading-relaxed mb-4">We collect information in the following ways:</p>
              
              <h3 className="text-lg font-semibold text-white mb-2">2.1 Information You Provide</h3>
              <ul className="space-y-2 text-[#888] mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Account Information:</strong> Name, email address, phone number when you create an account or place an order</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Vehicle Information:</strong> Make, model, year, engine type, and ECU details for service purposes</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">ECU Files:</strong> Original ECU files you upload for modification</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Payment Information:</strong> Processed securely through PayPal (we do not store credit card details)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Communication Data:</strong> Messages, inquiries, and support requests you send us</span>
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-white mb-2">2.2 Automatically Collected Information</h3>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Device Information:</strong> IP address, browser type, operating system</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Usage Data:</strong> Pages visited, time spent on site, click patterns</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Cookies:</strong> Session cookies for functionality and analytics cookies for site improvement</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-[#888] leading-relaxed mb-4">We use the collected information for:</p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Service Delivery:</strong> Processing your ECU file modification orders</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Communication:</strong> Sending order confirmations, updates, and responding to inquiries</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Quality Assurance:</strong> Maintaining records of modifications for warranty support</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Site Improvement:</strong> Analyzing usage patterns to enhance our services</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Security:</strong> Protecting against fraud and unauthorized access</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Legal Compliance:</strong> Meeting our legal and regulatory obligations</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">4. Data Storage & Security</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                We implement industry-standard security measures to protect your personal information:
              </p>
              <ul className="space-y-2 text-[#888] mb-4">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>SSL/TLS encryption for all data transmission</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Secure cloud storage with access controls</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Regular security audits and updates</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Limited employee access to personal data on a need-to-know basis</span>
                </li>
              </ul>
              <p className="text-[#888] leading-relaxed">
                ECU files are stored securely and retained for a period of 12 months to facilitate warranty support and rework requests. After this period, files may be deleted from our systems.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">5. Information Sharing</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Service Providers:</strong> Payment processors (PayPal) and hosting providers who assist in our operations</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Legal Requirements:</strong> When required by law or to respond to legal process</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Protection:</strong> To protect our rights, privacy, safety, or property</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">6. Cookies Policy</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                Our website uses cookies to enhance your browsing experience:
              </p>
              <ul className="space-y-2 text-[#888] mb-4">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Essential Cookies:</strong> Required for basic site functionality and security</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Analytics Cookies:</strong> Help us understand how visitors interact with our site</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Preference Cookies:</strong> Remember your settings and preferences</span>
                </li>
              </ul>
              <p className="text-[#888] leading-relaxed">
                You can control cookie settings through your browser preferences. Note that disabling certain cookies may affect site functionality.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">7. Your Rights</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                Depending on your location, you may have the following rights regarding your personal data:
              </p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Access:</strong> Request a copy of your personal data we hold</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete data</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Portability:</strong> Request transfer of your data in a machine-readable format</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Objection:</strong> Object to certain processing of your data</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Withdraw Consent:</strong> Withdraw previously given consent at any time</span>
                </li>
              </ul>
              <p className="text-[#888] leading-relaxed mt-4">
                To exercise any of these rights, please contact us through our <Link to="/contact" className="text-[#00F0FF] hover:underline">Contact Page</Link>.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">8. Third-Party Links</h2>
              <p className="text-[#888] leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">9. Children&apos;s Privacy</h2>
              <p className="text-[#888] leading-relaxed">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a minor, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">10. International Data Transfers</h2>
              <p className="text-[#888] leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our services, you consent to such transfers. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">11. Changes to This Policy</h2>
              <p className="text-[#888] leading-relaxed">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically. Your continued use of our services after any changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">12. Contact Us</h2>
              <p className="text-[#888] leading-relaxed">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us through our <Link to="/contact" className="text-[#00F0FF] hover:underline">Contact Page</Link>.
              </p>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
